const cloneJsonLike = function (value) {
  if (Array.isArray(value)) {
    return value.map((item) => cloneJsonLike(item))
  }
  if (!value || typeof value !== 'object') return value
  const out = {}
  Object.keys(value).forEach((key) => {
    out[key] = cloneJsonLike(value[key])
  })
  return out
}

const normalizePointProjectPropsEntry = function (entry) {
  if (!entry || typeof entry !== 'object' || Array.isArray(entry)) return null
  const cloned = cloneJsonLike(entry)
  return Object.keys(cloned).length ? cloned : null
}

const capturePointProjectProps = function (importMissionState) {
  const points = (importMissionState && Array.isArray(importMissionState.waypoints)) ? importMissionState.waypoints : []
  if (!points.length) return null
  const rows = points.map((point) => {
    const projectProps = point && point.props && point.props.project
    return normalizePointProjectPropsEntry(projectProps)
  })
  return rows.some((row) => !!row) ? rows : null
}

const applyPointProjectPropsToWaypoints = function (pointProjectProps, waypoints) {
  if (!Array.isArray(waypoints) || !waypoints.length) return
  const rows = Array.isArray(pointProjectProps) ? pointProjectProps : []
  waypoints.forEach((point, idx) => {
    if (!point || typeof point !== 'object') return
    const nextProjectProps = normalizePointProjectPropsEntry(rows[idx])
    if (!point.props || typeof point.props !== 'object') {
      point.props = {}
    }
    if (nextProjectProps) {
      point.props.project = nextProjectProps
      return
    }
    if (point.props && Object.prototype.hasOwnProperty.call(point.props, 'project')) {
      delete point.props.project
    }
  })
}

const buildMinimalVariantSnapshot = function (snapshot) {
  if (!snapshot || typeof snapshot !== 'object') return null
  const missionType = (snapshot.missionType === 'kmz') ? 'kmz' : 'kml'
  const missionXml = (typeof snapshot.missionXml === 'string' && snapshot.missionXml.trim()) ? snapshot.missionXml : null
  if (!missionXml) return null
  const missionWaylinesXml = (typeof snapshot.missionWaylinesXml === 'string' && snapshot.missionWaylinesXml.trim())
    ? snapshot.missionWaylinesXml
    : null
  if (missionType === 'kmz' && !missionWaylinesXml) return null
  const pointProjectProps = Array.isArray(snapshot.pointProjectProps)
    ? snapshot.pointProjectProps.map((entry) => normalizePointProjectPropsEntry(entry))
    : null
  return {
    missionType: missionType,
    missionXml: missionXml,
    missionWaylinesXml: missionWaylinesXml,
    pointProjectProps: pointProjectProps
  }
}

const sanitizeShadowBundleForSnapshot = function (shadowBundle, currentSnapshot) {
  if (!shadowBundle || typeof shadowBundle !== 'object') return null
  const bundle = cloneJsonLike(shadowBundle)
  if (bundle.histories && typeof bundle.histories === 'object') {
    delete bundle.histories
  }
  const activeRaw = String(bundle.activeKey || 'K').trim().toUpperCase()
  const activeKey = (activeRaw === 'B' || activeRaw === 'J') ? activeRaw : 'K'
  bundle.activeKey = activeKey
  const variantsRaw = (bundle.variants && typeof bundle.variants === 'object') ? bundle.variants : {}
  const variants = {}
  Object.keys(variantsRaw).forEach((keyRaw) => {
    const key = String(keyRaw || '').trim().toUpperCase()
    if (!(key === 'B' || key === 'K' || key === 'J')) return
    const minimal = buildMinimalVariantSnapshot(variantsRaw[keyRaw])
    if (!minimal) return
    variants[key] = minimal
  })
  const currentMinimal = buildMinimalVariantSnapshot(currentSnapshot)
  if (currentMinimal && Object.prototype.hasOwnProperty.call(variants, activeKey)) {
    delete variants[activeKey]
  }
  bundle.variants = variants
  return bundle
}

const captureState = function (importMissionType, importMissionState) {
  const serializer = new XMLSerializer()
  const missionType = (importMissionType === 'kmz') ? 'kmz' : 'kml'
  const missionXml = (importMissionState && importMissionState.xmlDoc) ? serializer.serializeToString(importMissionState.xmlDoc) : null
  const missionWaylinesXml = (missionType === 'kmz' && importMissionState && importMissionState.secondaryXmlDoc)
    ? serializer.serializeToString(importMissionState.secondaryXmlDoc)
    : null
  const pointProjectProps = capturePointProjectProps(importMissionState)
  const currentSnapshot = {
    missionType: missionType,
    missionXml: missionXml,
    missionWaylinesXml: missionWaylinesXml,
    pointProjectProps: pointProjectProps ? cloneJsonLike(pointProjectProps) : null
  }
  const shadowBundle = sanitizeShadowBundleForSnapshot(
    (importMissionState && importMissionState.__shadowBundle && typeof importMissionState.__shadowBundle === 'object')
      ? importMissionState.__shadowBundle
      : null,
    currentSnapshot
  )
  return {
    missionType: missionType,
    missionXml: missionXml,
    missionWaylinesXml: missionWaylinesXml,
    pointProjectProps: pointProjectProps,
    shadowBundle: shadowBundle
  }
}

const normalizeStateSnapshot = function (snapshot) {
  if (!snapshot || typeof snapshot !== 'object') return null
  const missionType = (snapshot.missionType === 'kmz') ? 'kmz' : 'kml'
  const missionXml = (typeof snapshot.missionXml === 'string' && snapshot.missionXml.trim()) ? snapshot.missionXml : null
  if (!missionXml) return null
  const missionWaylinesXml = (typeof snapshot.missionWaylinesXml === 'string' && snapshot.missionWaylinesXml.trim())
    ? snapshot.missionWaylinesXml
    : null
  if (missionType === 'kmz' && !missionWaylinesXml) return null
  const pointProjectProps = Array.isArray(snapshot.pointProjectProps)
    ? snapshot.pointProjectProps.map((entry) => normalizePointProjectPropsEntry(entry))
    : null
  const shadowBundle = sanitizeShadowBundleForSnapshot(snapshot.shadowBundle, {
    missionType: missionType,
    missionXml: missionXml,
    missionWaylinesXml: missionWaylinesXml,
    pointProjectProps: pointProjectProps
  })
  return {
    missionType: missionType,
    missionXml: missionXml,
    missionWaylinesXml: missionWaylinesXml,
    pointProjectProps: pointProjectProps,
    shadowBundle: shadowBundle
  }
}

const normalizeHistoryStack = function (stack, importHistoryMax, fnNormalizeStateSnapshot) {
  if (!Array.isArray(stack)) return []
  return stack
    .map((entry) => fnNormalizeStateSnapshot(entry))
    .filter((entry) => !!entry)
    .slice(-importHistoryMax)
}

const persistImportDraftNow = async function (ctx) {
  const windowRef = ctx.windowRef
  if (!windowRef || !windowRef.idbKeyval) return
  const current = ctx.fnNormalizeStateSnapshot(ctx.fnCaptureState())
  if (!current) {
    try {
      await windowRef.idbKeyval.del(ctx.importDraftKey)
    } catch (error) {
      console.error(ctx.fnT('log.indexeddb_draft_delete_error'), error)
    }
    return
  }
  const draft = {
    version: 1,
    updatedAt: Date.now(),
    importKmlFilename: ctx.importKmlFilename || null,
    current: current,
    history: {
      undo: ctx.fnNormalizeHistoryStack(ctx.importHistory.undo),
      redo: ctx.fnNormalizeHistoryStack(ctx.importHistory.redo)
    }
  }
  try {
    await windowRef.idbKeyval.set(ctx.importDraftKey, draft)
  } catch (error) {
    const message = String((error && error.message) || '').toLowerCase()
    const isTooLarge = message.includes('structured clone is too large') || message.includes('the structured clone is too large')
    if (!isTooLarge) {
      console.error(ctx.fnT('log.indexeddb_draft_save_error'), error)
      return
    }
    try {
      const compactCurrent = cloneJsonLike(current)
      if (compactCurrent && typeof compactCurrent === 'object') {
        delete compactCurrent.shadowBundle
      }
      await windowRef.idbKeyval.set(ctx.importDraftKey, {
        version: 1,
        updatedAt: Date.now(),
        importKmlFilename: ctx.importKmlFilename || null,
        current: compactCurrent,
        history: { undo: [], redo: [] }
      })
    } catch (compactError) {
      console.error(ctx.fnT('log.indexeddb_draft_save_error'), compactError)
    }
  }
}

const clearPersistedImportDraft = async function (ctx) {
  if (typeof ctx.fnGetImportDraftPersistTimer === 'function' && typeof ctx.fnSetImportDraftPersistTimer === 'function') {
    const timer = ctx.fnGetImportDraftPersistTimer()
    if (timer) {
      clearTimeout(timer)
      ctx.fnSetImportDraftPersistTimer(null)
    }
  }
  const windowRef = ctx.windowRef
  if (!windowRef || !windowRef.idbKeyval) return
  try {
    await windowRef.idbKeyval.del(ctx.importDraftKey)
  } catch (error) {
    console.error(ctx.fnT('log.indexeddb_draft_clear_error'), error)
  }
}

const restoreState = function (state, ctx) {
  if (!state) return
  ctx.fnSetImportHistoryLock(true)
  ctx.importItems.clearLayers()
  ctx.importLineItems.clearLayers()
  ctx.fnSetImportMissionState(null)
  ctx.fnSetImportSelectedPoints([])

  if (state.missionXml) {
    const parsedMission = (state.missionType === 'kmz')
      ? ctx.fnParseKmzMission(state.missionXml, state.missionWaylinesXml)
      : ctx.fnParseKmlMission(state.missionXml)
    applyPointProjectPropsToWaypoints(state.pointProjectProps, parsedMission && parsedMission.waypoints)
    if (state.shadowBundle && typeof state.shadowBundle === 'object' && parsedMission && typeof parsedMission === 'object') {
      parsedMission.__shadowBundle = cloneJsonLike(state.shadowBundle)
      parsedMission.__shadowActiveKey = String(state.shadowBundle.activeKey || 'K').trim().toUpperCase()
    }
    const filenameFallback = (state.missionType === 'kmz') ? 'mission.kmz' : 'mission.kml'
    ctx.fnSetImportMissionType((state.missionType === 'kmz') ? 'kmz' : 'kml')
    ctx.fnSetTemplateWaypointDefaultsCache(null)
    const currentFilename = ctx.fnGetImportKmlFilename()
    ctx.fnSetImportKmlFilename(currentFilename || filenameFallback)
    ctx.fnRenderImportLines([parsedMission.lineCoords], parsedMission.waypoints, 'mission', {
      xmlDoc: parsedMission.xmlDoc,
      documentEl: parsedMission.documentEl,
      folderEl: parsedMission.folderEl,
      importPlacemarkEl: parsedMission.importPlacemarkEl,
      importCoordEl: parsedMission.importCoordEl,
      secondaryXmlDoc: parsedMission.secondaryXmlDoc,
      secondaryDocumentEl: parsedMission.secondaryDocumentEl,
      secondaryFolderEl: parsedMission.secondaryFolderEl,
      polygons: parsedMission.polygons
    })
    const restoredMissionState = ctx.fnGetImportMissionState()
    const restoredContext = ctx.fnGetMissionAltitudeContextFor3d(restoredMissionState)
    if (ctx.fnRecalculateEgm96EllipsoidForPoints((restoredMissionState && restoredMissionState.waypoints) || [], restoredContext) > 0) {
      ctx.fnScheduleRoute3dSharedSync()
    }
  }
  ctx.fnSetImportHistoryLock(false)
}

const historyPush = function (snapshot, ctx) {
  if (ctx.fnGetImportHistoryLock()) return
  let nextSnapshot = snapshot
  if (!nextSnapshot) nextSnapshot = ctx.fnCaptureState()
  const safeSnapshot = ctx.fnNormalizeStateSnapshot(nextSnapshot)
  if (!safeSnapshot) return
  ctx.importHistory.undo.push(safeSnapshot)
  if (ctx.importHistory.undo.length > ctx.importHistoryMax) {
    ctx.importHistory.undo = ctx.importHistory.undo.slice(-ctx.importHistoryMax)
  }
  ctx.importHistory.redo = []
  ctx.fnSchedulePersistImportDraft()
}

const historyUndo = function (ctx) {
  if (!ctx.importHistory.undo.length) return
  const current = ctx.fnCaptureState()
  const prev = ctx.importHistory.undo.pop()
  const safeCurrent = ctx.fnNormalizeStateSnapshot(current)
  if (safeCurrent) {
    ctx.importHistory.redo.push(safeCurrent)
    if (ctx.importHistory.redo.length > ctx.importHistoryMax) {
      ctx.importHistory.redo = ctx.importHistory.redo.slice(-ctx.importHistoryMax)
    }
  }
  ctx.fnRestoreState(prev)
  ctx.fnSchedulePersistImportDraft()
}

const historyRedo = function (ctx) {
  if (!ctx.importHistory.redo.length) return
  const current = ctx.fnCaptureState()
  const next = ctx.importHistory.redo.pop()
  const safeCurrent = ctx.fnNormalizeStateSnapshot(current)
  if (safeCurrent) {
    ctx.importHistory.undo.push(safeCurrent)
    if (ctx.importHistory.undo.length > ctx.importHistoryMax) {
      ctx.importHistory.undo = ctx.importHistory.undo.slice(-ctx.importHistoryMax)
    }
  }
  ctx.fnRestoreState(next)
  ctx.fnSchedulePersistImportDraft()
}

const getPointName = function (point) {
  if (point && point.props && point.props.project && typeof point.props.project === 'object') {
    const projectPointName = String(point.props.project.pointName || '').trim()
    if (projectPointName) return projectPointName
  }
  if (point && point.props) {
    const raw = point.props.name
    const propsName = Array.isArray(raw) ? String(raw[0] || '') : String(raw || '')
    if (propsName.trim()) return propsName
  }
  const xmlNodes = []
  if (point && point.xmlEl) xmlNodes.push(point.xmlEl)
  if (point && Array.isArray(point.linkedXmlEls)) {
    point.linkedXmlEls.forEach((node) => {
      if (node) xmlNodes.push(node)
    })
  }
  for (let i = 0; i < xmlNodes.length; i += 1) {
    const node = xmlNodes[i]
    if (!node || typeof node.getElementsByTagName !== 'function') continue
    const nameEl = node.getElementsByTagName('name')[0]
    const xmlName = String(nameEl && nameEl.textContent || '').trim()
    if (xmlName) return xmlName
  }
  return ''
}

const setPointName = function (point, name, fnSyncPointActionFileSuffixWithNameWithContext) {
  if (!point || name === undefined || name === null) return
  if (!point.props || typeof point.props !== 'object') {
    point.props = {}
  }
  if (!point.props.project || typeof point.props.project !== 'object' || Array.isArray(point.props.project)) {
    point.props.project = {}
  }
  const nextName = String(name)
  point.props.project.pointName = nextName
  if (Object.prototype.hasOwnProperty.call(point.props.project, 'name')) {
    delete point.props.project.name
  }
  fnSyncPointActionFileSuffixWithNameWithContext(point, nextName)
}

const renameWaypointsLinear = function (waypoints, options, fnSetPointName, fnSetPointDescription, fnGetWaypointTooltipText) {
  const points = Array.isArray(waypoints) ? waypoints.filter((pt) => !!pt) : []
  if (!points.length) return false
  const opts = Object.assign({
    start: 1,
    prefix: 'S',
    syncDescription: true
  }, options || {})
  let counter = Math.max(1, Math.floor(Number(opts.start) || 1))
  const safePrefix = String(opts.prefix || 'S').trim() || 'S'
  points.forEach((pt) => {
    const code = safePrefix + '-' + counter
    fnSetPointName(pt, code)
    if (opts.syncDescription) {
      fnSetPointDescription(pt, code)
    }
    if (pt.marker && typeof pt.marker.setTooltipContent === 'function') {
      pt.marker.setTooltipContent(fnGetWaypointTooltipText(pt))
    }
    counter += 1
  })
  return true
}

const getPointDescription = function (point) {
  if (!point || !point.props) return ''
  const raw = point.props.description
  if (raw === null || raw === undefined) return ''
  if (typeof raw === 'string') return raw
  if (Array.isArray(raw)) return String(raw[0] || '')
  if (typeof raw === 'object') {
    if (raw._text !== undefined && raw._text !== null) return String(raw._text)
  }
  return String(raw)
}

const setPointDescription = function (point, description, fnGetPointXmlNodes, fnUpdateXmlByPath, importMissionType, documentRef) {
  if (!point) return false
  const nextText = (description === null || description === undefined) ? '' : String(description)
  if (!point.props || typeof point.props !== 'object') {
    point.props = {}
  }
  const prev = point.props.description
  if (prev && typeof prev === 'object' && !Array.isArray(prev)) {
    prev._text = nextText
  } else {
    point.props.description = nextText
  }
  const xmlNodes = fnGetPointXmlNodes(point)
  if (!xmlNodes.length) return true
  if (xmlNodes.some((node) => fnUpdateXmlByPath(node, 'description._text', nextText))) return true
  if (xmlNodes.some((node) => fnUpdateXmlByPath(node, 'description', nextText))) return true
  if (importMissionType === 'kmz') return true
  const targetNode = xmlNodes[0]
  const ownerDocument = targetNode.ownerDocument || documentRef
  if (!ownerDocument) return false
  let descriptionEl = targetNode.getElementsByTagName('description')[0]
  if (!descriptionEl) {
    descriptionEl = ownerDocument.createElement('description')
    const nameEl = targetNode.getElementsByTagName('name')[0]
    if (nameEl && nameEl.parentNode === targetNode) {
      if (nameEl.nextSibling) {
        targetNode.insertBefore(descriptionEl, nameEl.nextSibling)
      } else {
        targetNode.appendChild(descriptionEl)
      }
    } else {
      targetNode.appendChild(descriptionEl)
    }
  }
  descriptionEl.textContent = nextText
  return true
}

const roundLetter = function (idx) {
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
  let n = Math.max(0, idx)
  let out = ''
  do {
    out = alphabet[n % 26] + out
    n = Math.floor(n / 26) - 1
  } while (n >= 0)
  return out
}

const padNumber = function (value, width) {
  const n = Math.max(0, Math.floor(Number(value) || 0))
  const w = Math.max(1, Math.floor(Number(width) || 1))
  return String(n).padStart(w, '0')
}

const applyDivideRowNames = function (renameRows, fnSetPointName, fnSetPointDescription, fnGetWaypointTooltipText, fnPadNumber) {
  if (!Array.isArray(renameRows) || !renameRows.length) return false
  const normalizedRows = renameRows.map((row) => {
    const points = Array.isArray(row.points) ? row.points.slice() : []
    return {
      segmentLetter: row.segmentLetter,
      isForward: !!row.isForward,
      points: points.map((entry) => {
        if (entry && entry.point) return entry
        return { point: entry, role: null }
      })
    }
  })
  const totalPoints = normalizedRows.reduce((sum, row) => {
    const c = row.points.length
    return sum + c
  }, 0)
  if (!totalPoints) return false

  for (let i = 0; i < normalizedRows.length - 1; i++) {
    const row = normalizedRows[i]
    const isReverseRow = (i % 2 === 1)
    if (!isReverseRow) continue
    const prevTotal = i > 0 ? normalizedRows[i - 1].points.length : 0
    const nextTotal = normalizedRows[i + 1].points.length
    const targetTotal = Math.max(row.points.length, prevTotal, nextTotal)
    const deficit = targetTotal - row.points.length
    if (deficit <= 0) continue
    const donor = normalizedRows[i + 1]
    const movable = Math.max(0, donor.points.length - 1)
    const moveCount = Math.min(deficit, movable)
    if (moveCount <= 0) continue
    const moved = donor.points.splice(0, moveCount)
    row.points = row.points.concat(moved)
  }

  const globalWidth = String(totalPoints).length
  let globalCounter = 1
  const actualRowTotals = normalizedRows.map((row) => {
    return row.points.length
  })

  normalizedRows.forEach((row, rowIdx) => {
    const rowPoints = row.points
    const actualRowTotal = rowPoints.length
    if (!actualRowTotal) return
    const isForwardRow = (rowIdx % 2 === 0)
    const prevTotal = rowIdx > 0 ? actualRowTotals[rowIdx - 1] : 0
    const nextTotal = rowIdx < (actualRowTotals.length - 1) ? actualRowTotals[rowIdx + 1] : 0
    const logicalRowTotal = isForwardRow
      ? actualRowTotal
      : Math.max(actualRowTotal, prevTotal, nextTotal)
    const rowWidth = String(logicalRowTotal).length
    const nameOrder = isForwardRow ? rowPoints : rowPoints.slice().reverse()
    nameOrder.forEach((entry, idx) => {
      const pt = entry && entry.point ? entry.point : entry
      if (!pt) return
      const localCounter = isForwardRow ? (idx + 1) : (logicalRowTotal - idx)
      const globalPart = fnPadNumber(globalCounter, globalWidth)
      const localPart = fnPadNumber(localCounter, rowWidth)
      const role = entry && entry.role ? entry.role : null
      const prefix = role === 'inserted' ? 'P' : 'S'
      const nextCode = prefix + '-' + globalPart + '-' + row.segmentLetter + localPart
      fnSetPointName(pt, nextCode)
      fnSetPointDescription(pt, nextCode)
      if (pt.marker && typeof pt.marker.setTooltipContent === 'function') {
        pt.marker.setTooltipContent(fnGetWaypointTooltipText(pt))
      }
      globalCounter += 1
    })
  })
  return true
}

const buildDivideRenameRowsFromCreatedPoints = function (createdRows) {
  const rows = []
  ;(createdRows || []).forEach((row) => {
    if (!row || !row.first || !row.second || !Array.isArray(row.insertedPoints)) return
    let points = [{ point: row.first, role: 'original' }]
      .concat(row.insertedPoints.map((pt) => ({ point: pt, role: 'inserted' })))
      .concat([{ point: row.second, role: 'original' }])
    if (!row.isForward) {
      points = [{ point: row.second, role: 'original' }]
        .concat(row.insertedPoints.slice().reverse().map((pt) => ({ point: pt, role: 'inserted' })))
        .concat([{ point: row.first, role: 'original' }])
    }
    if (!points.length) return
    rows.push({
      segmentLetter: row.segmentLetter,
      isForward: !!row.isForward,
      points: points
    })
  })
  return rows
}

export {
  captureState,
  normalizeStateSnapshot,
  normalizeHistoryStack,
  persistImportDraftNow,
  clearPersistedImportDraft,
  restoreState,
  historyPush,
  historyUndo,
  historyRedo,
  applyPointProjectPropsToWaypoints,
  getPointName,
  setPointName,
  renameWaypointsLinear,
  getPointDescription,
  setPointDescription,
  roundLetter,
  padNumber,
  applyDivideRowNames,
  buildDivideRenameRowsFromCreatedPoints
}
